import React, { useState, useEffect, useCallback, useContext } from "react";
import { toast } from "react-toastify";
import {
  FaCog,
  FaPhoneAlt,
  FaKey,
  FaDatabase,
  FaQuestionCircle,
  FaSlidersH,
  FaHistory,
  FaTrash,
  FaEdit,
  FaPlus,
  FaFileUpload,
  FaAlignLeft,
  FaEye,
  FaEyeSlash,
  FaChevronLeft,
  FaChevronRight,
  FaShieldAlt,
} from "react-icons/fa";
import { API_URL } from "../../Constants"; // adjust path if needed
import { AuthContext } from "../context/AuthContext"; // adjust path if needed

/* ------------------------------------------------------------------ */
/*  Config                                                              */
/* ------------------------------------------------------------------ */
const API_BASE = API_URL;
const getToken = () =>
  localStorage.getItem("token") || sessionStorage.getItem("token");

/* ------------------------------------------------------------------ */
/*  API helper                                                          */
/* ------------------------------------------------------------------ */
const api = async (path, { method = "GET", body, formData } = {}) => {
  const headers = { Authorization: `Bearer ${getToken()}` };
  if (!formData) headers["Content-Type"] = "application/json";

  const res = await fetch(`${API_BASE}/api/support/admin${path}`, {
    method,
    headers,
    body: formData ? formData : body ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  let data = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = { message: text };
    }
  }
  if (!res.ok) {
    const err = new Error(data?.message || "Something went wrong. Please try again.");
    err.status = res.status;
    throw err;
  }
  return data;
};

/* ------------------------------------------------------------------ */
/*  Shared style tokens (same as AI Configuration page)                 */
/* ------------------------------------------------------------------ */
const card =
  "p-5 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700";
const labelCls = "block text-xs font-medium mb-1 dark:text-gray-300";
const inputCls =
  "w-full px-2.5 py-1.5 text-xs border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 focus:outline-none focus:ring-2 focus:ring-[#8B5CF6]";
const headingCls =
  "cfg-heading text-sm font-semibold text-gray-800 dark:text-white";
const hintCls = "text-xs text-gray-500 dark:text-gray-400";
const btnPrimary =
  "px-4 py-1.5 text-xs bg-[#673AB7] text-white rounded hover:bg-[#5E35A6] disabled:opacity-50 transition-colors h-[32px] flex items-center justify-center gap-1.5";
const btnSecondary =
  "px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 transition-colors h-[32px] flex items-center justify-center gap-1.5";
const btnDanger =
  "px-4 py-1.5 text-xs border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 rounded hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-50 transition-colors h-[32px] flex items-center justify-center gap-1.5";
const pill =
  "text-[10px] font-normal px-2 py-0.5 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#5E35A6] dark:text-[#C4B5FD] rounded-full";
const rowCls =
  "px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded";

const formatDate = (value) => {
  if (!value) return "-";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

/* ------------------------------------------------------------------ */
/*  Small reusable pieces                                               */
/* ------------------------------------------------------------------ */
const Toggle = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    className={`relative w-8 h-4 rounded-full transition-colors ${
      checked ? "bg-[#673AB7]" : "bg-gray-300 dark:bg-gray-600"
    }`}
  >
    <span
      className={`absolute top-[2px] left-[2px] w-3 h-3 bg-white rounded-full transition-transform ${
        checked ? "translate-x-4" : ""
      }`}
    />
  </button>
);

const LoadingBox = ({ text = "Loading..." }) => (
  <div className="p-8 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400">
    {text}
  </div>
);

const Modal = ({ title, onClose, wide, children, footer }) => (
  <div
    className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
    onClick={onClose}
  >
    <div
      className={`bg-white dark:bg-gray-800 rounded-lg shadow-lg w-full p-5 ${
        wide ? "max-w-2xl" : "max-w-md"
      }`}
      onClick={(e) => e.stopPropagation()}
    >
      <h3 className={`${headingCls} mb-2`}>{title}</h3>
      <div className={wide ? "max-h-[60vh] overflow-y-auto" : ""}>{children}</div>
      {footer && <div className="flex justify-end gap-2 mt-5">{footer}</div>}
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/*  Tab: General                                                        */
/* ------------------------------------------------------------------ */
const GeneralTab = ({ config, save, saving }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      active: config.active,
      welcomeMessage: config.welcomeMessage || "",
      systemPrompt: config.systemPrompt || "",
      model: config.model || "",
      temperature: config.temperature ?? 0.3,
    });
  }, [config]);

  return (
    <div className={card}>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className={headingCls}>General Settings</h3>
          <p className={`${hintCls} mt-1`}>
            Control how the support assistant introduces itself and responds.
          </p>
        </div>
        <label className="flex items-center gap-2 cursor-pointer select-none flex-shrink-0">
          <span className="text-xs text-gray-600 dark:text-gray-300">
            {f.active ? "Active" : "Inactive"}
          </span>
          <Toggle
            checked={!!f.active}
            onChange={() => setF((p) => ({ ...p, active: !p.active }))}
          />
        </label>
      </div>

      <div className="space-y-3">
        <div>
          <label className={labelCls}>Welcome Message</label>
          <textarea
            rows={2}
            className={`${inputCls} resize-y`}
            value={f.welcomeMessage || ""}
            onChange={(e) => setF({ ...f, welcomeMessage: e.target.value })}
          />
        </div>

        <div>
          <label className={labelCls}>System Prompt</label>
          <textarea
            rows={6}
            className={`${inputCls} resize-y`}
            value={f.systemPrompt || ""}
            onChange={(e) => setF({ ...f, systemPrompt: e.target.value })}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Saving an empty prompt restores the default system prompt.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Model</label>
            <input
              className={inputCls}
              value={f.model || ""}
              onChange={(e) => setF({ ...f, model: e.target.value })}
            />
          </div>
          <div>
            <label className={labelCls}>
              Temperature ({Number(f.temperature ?? 0).toFixed(1)})
            </label>
            <input
              type="range"
              min="0"
              max="2"
              step="0.1"
              className="w-full accent-purple-600 mt-2"
              value={f.temperature ?? 0.3}
              onChange={(e) =>
                setF({ ...f, temperature: parseFloat(e.target.value) })
              }
            />
          </div>
        </div>

        <div className="pt-1">
          <button className={btnPrimary} disabled={saving} onClick={() => save(f)}>
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tab: Phone                                                          */
/* ------------------------------------------------------------------ */
const PhoneTab = ({ config, save, saving }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      supportPhoneNumber: config.supportPhoneNumber || "",
      supportHours: config.supportHours || "",
      callPromptMessage: config.callPromptMessage || "",
      fallbackMessage: config.fallbackMessage || "",
    });
  }, [config]);

  const phoneInvalid =
    f.supportPhoneNumber &&
    !/^[\d\s+\-()]{5,25}$/.test(f.supportPhoneNumber.trim());

  return (
    <div className={card}>
      <h3 className={headingCls}>Support Contact</h3>
      <p className={`${hintCls} mt-1 mb-4`}>
        Shown to users when the assistant cannot resolve their issue or when they
        ask to speak to a person.
      </p>

      <div className="space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className={labelCls}>Support Phone Number</label>
            <input
              className={`${inputCls} ${phoneInvalid ? "border-red-400" : ""}`}
              placeholder="+91 00000 00000"
              value={f.supportPhoneNumber || ""}
              onChange={(e) => setF({ ...f, supportPhoneNumber: e.target.value })}
            />
            {phoneInvalid ? (
              <p className="text-[11px] text-red-500 mt-1">
                Enter 5–25 characters using digits, spaces, +, - and ( ).
              </p>
            ) : (
              <p className="text-[11px] text-gray-400 mt-1">
                Leave empty to remove the number.
              </p>
            )}
          </div>
          <div>
            <label className={labelCls}>Support Hours</label>
            <input
              className={inputCls}
              placeholder="Mon-Sat, 10am-6pm"
              value={f.supportHours || ""}
              onChange={(e) => setF({ ...f, supportHours: e.target.value })}
            />
            <p className="text-[11px] text-gray-400 mt-1">
              Leave empty to remove the hours.
            </p>
          </div>
        </div>

        <div>
          <label className={labelCls}>Call Prompt Message</label>
          <textarea
            rows={2}
            className={`${inputCls} resize-y`}
            value={f.callPromptMessage || ""}
            onChange={(e) => setF({ ...f, callPromptMessage: e.target.value })}
          />
        </div>

        <div>
          <label className={labelCls}>Fallback Message</label>
          <textarea
            rows={2}
            className={`${inputCls} resize-y`}
            value={f.fallbackMessage || ""}
            onChange={(e) => setF({ ...f, fallbackMessage: e.target.value })}
          />
          <p className="text-[11px] text-gray-400 mt-1">
            Displayed when the assistant is unable to generate a response.
          </p>
        </div>

        <div className="pt-1">
          <button
            className={btnPrimary}
            disabled={saving || phoneInvalid}
            onClick={() => save(f)}
          >
            {saving ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tab: AI key                                                         */
/* ------------------------------------------------------------------ */
const KeyTab = ({ keyInfo, refresh, askConfirm }) => {
  const [apiKey, setApiKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState("");
  const [testResult, setTestResult] = useState(null);

  const run = async (name, fn) => {
    setBusy(name);
    try {
      await fn();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const saveKey = () =>
    run("save", async () => {
      await api("/api-key", { method: "PUT", body: { apiKey: apiKey.trim() } });
      setApiKey("");
      setShowKey(false);
      setTestResult(null);
      await refresh();
      toast.success("Support API Key saved successfully");
    });

  const deleteKey = () =>
    askConfirm({
      title: "Delete API key?",
      message:
        "The support assistant will stop responding until a new API key is saved. This action cannot be undone.",
      onConfirm: () =>
        run("delete", async () => {
          const r = await api("/api-key", { method: "DELETE" });
          setTestResult(null);
          await refresh();
          toast.success(r?.message || "API key deleted successfully");
        }),
    });

  const test = () =>
    run("test", async () => {
      setTestResult(await api("/test-connection", { method: "POST" }));
    });

  return (
    <div className={card}>
      <h3 className={headingCls}>Gemini API Configuration</h3>
      <p className={`${hintCls} mt-1 mb-4`}>
        This key powers the support assistant. For security, a saved key is never
        displayed again.
      </p>

      {keyInfo.apiKeyError && (
        <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-600 dark:text-red-300 mb-4">
          {keyInfo.apiKeyError} The saved key could not be read. Please save the key
          again.
        </div>
      )}

      <p className="text-xs text-gray-700 dark:text-gray-300 mb-4">
        Status:{" "}
        {keyInfo.hasApiKey ? (
          <span className="font-mono px-2 py-0.5 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#5E35A6] dark:text-[#C4B5FD] rounded">
            {keyInfo.maskedApiKey}
          </span>
        ) : (
          <span className="text-red-500 font-medium">Not configured</span>
        )}
      </p>

      <div className="flex flex-col md:flex-row gap-3 items-end">
        <div className="flex-1 w-full relative">
          <label className={labelCls}>
            {keyInfo.hasApiKey ? "Replace API Key" : "API Key"}
          </label>
          <input
            type={showKey ? "text" : "password"}
            autoComplete="new-password"
            placeholder="Enter Support API Key (AIza...)"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className={inputCls}
          />
          <button
            type="button"
            className="absolute right-2.5 top-[26px] text-gray-500 dark:text-gray-400 hover:text-gray-700"
            onClick={() => setShowKey(!showKey)}
          >
            {showKey ? <FaEyeSlash size={12} /> : <FaEye size={12} />}
          </button>
        </div>
        <button
          className={btnPrimary}
          disabled={!apiKey.trim() || !!busy}
          onClick={saveKey}
        >
          {busy === "save" ? "Saving..." : "Save Key"}
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          className={btnSecondary}
          disabled={!keyInfo.hasApiKey || !!busy}
          onClick={test}
        >
          {busy === "test" ? "Testing..." : "Test Connection"}
        </button>
        <button
          className={btnDanger}
          disabled={!keyInfo.hasApiKey || !!busy}
          onClick={deleteKey}
        >
          <FaTrash size={10} /> Delete Key
        </button>
      </div>

      {testResult && (
        <div
          className={`mt-4 p-3 rounded text-xs border ${
            testResult.success
              ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300"
              : "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800 text-red-600 dark:text-red-300"
          }`}
        >
          {testResult.success
            ? "Connection successful. The API key is working."
            : testResult.message}
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tab: Knowledge base                                                 */
/* ------------------------------------------------------------------ */
const MAX_TOTAL = 800000;
const MAX_DOC = 300000;

const KnowledgeTab = ({ refresh, askConfirm }) => {
  const [items, setItems] = useState(null);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [file, setFile] = useState(null);
  const [fileTitle, setFileTitle] = useState("");
  const [busy, setBusy] = useState("");
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    try {
      setItems((await api("/knowledge")) || []);
    } catch (e) {
      toast.error(e.message);
      setItems([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const total = (items || []).reduce((s, i) => s + (i.characterCount || 0), 0);

  const addText = async () => {
    setBusy("text");
    try {
      await api("/knowledge/text", {
        method: "POST",
        body: { title: title.trim(), content },
      });
      setTitle("");
      setContent("");
      toast.success("Text added to the knowledge base");
      await load();
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const upload = async () => {
    setBusy("upload");
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (fileTitle.trim()) fd.append("title", fileTitle.trim());
      await api("/knowledge/upload", { method: "POST", formData: fd });
      setFile(null);
      setFileTitle("");
      toast.success("File uploaded successfully");
      await load();
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy("");
    }
  };

  const remove = (doc) =>
    askConfirm({
      title: `Delete "${doc.title}"?`,
      message:
        "This will permanently remove the document from the knowledge base. This action cannot be undone.",
      onConfirm: async () => {
        try {
          await api(`/knowledge/${doc.id}`, { method: "DELETE" });
          toast.success("Document deleted successfully");
          await load();
          refresh();
        } catch (e) {
          toast.error(e.message);
        }
      },
    });

  return (
    <div className="space-y-5">
      <div className={`${card} space-y-5`}>
        <div>
          <h3 className={`${headingCls} flex items-center gap-2`}>
            <FaDatabase size={12} /> Knowledge Base
            {(items || []).length > 0 && <span className={pill}>{items.length}</span>}
          </h3>
          <p className={`${hintCls} mt-1`}>
            Add LMS guides and documentation for the assistant to answer from. You
            can paste text or upload PDF and Word files. Maximum{" "}
            {MAX_DOC.toLocaleString()} characters per document and{" "}
            {MAX_TOTAL.toLocaleString()} in total.
          </p>
        </div>

        {/* Pasted text */}
        <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-100 dark:border-gray-700">
          <label className="flex items-center gap-1.5 text-xs font-medium mb-2 dark:text-gray-300">
            <FaAlignLeft size={10} /> Add Pasted Text
          </label>
          <input
            type="text"
            placeholder="Document title, e.g. Adding a new lead"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`${inputCls} mb-2`}
          />
          <textarea
            rows={5}
            placeholder="Paste the content here..."
            value={content}
            onChange={(e) => setContent(e.target.value)}
            className={`${inputCls} resize-y mb-2`}
          />
          <div className="flex items-center justify-between">
            <span
              className={`text-[11px] ${
                content.length > MAX_DOC ? "text-red-500" : "text-gray-400"
              }`}
            >
              {content.length.toLocaleString()} / {MAX_DOC.toLocaleString()}{" "}
              characters
            </span>
            <button
              className={btnPrimary}
              disabled={
                !title.trim() || !content.trim() || content.length > MAX_DOC || !!busy
              }
              onClick={addText}
            >
              <FaPlus size={10} /> {busy === "text" ? "Adding..." : "Add Text"}
            </button>
          </div>
        </div>

        {/* File upload */}
        <div className="p-3 bg-gray-50 dark:bg-gray-900/40 rounded border border-gray-100 dark:border-gray-700">
          <label className="flex items-center gap-1.5 text-xs font-medium mb-2 dark:text-gray-300">
            <FaFileUpload size={10} /> Upload File (PDF, DOCX)
          </label>
          <input
            type="text"
            placeholder="Title (optional)"
            value={fileTitle}
            onChange={(e) => setFileTitle(e.target.value)}
            className={`${inputCls} mb-2`}
          />
          <div className="flex items-center gap-2">
            <input
              type="file"
              accept=".pdf,.doc,.docx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="flex-1 text-xs text-gray-600 dark:text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:text-xs file:rounded file:border-0 file:bg-[#F3E8FF] file:text-[#5E35A6] hover:file:bg-[#E9D5FF]"
            />
            <button
              className={btnPrimary}
              disabled={!file || !!busy}
              onClick={upload}
            >
              {busy === "upload" ? "Uploading..." : "Upload"}
            </button>
          </div>
        </div>
      </div>

      {/* Document list */}
      <div className={card}>
        <div className="flex items-center justify-between mb-2.5">
          <h4 className="cfg-heading text-xs font-medium dark:text-gray-300">
            Documents
          </h4>
          <span className="text-[11px] text-gray-400">
            {total.toLocaleString()} / {MAX_TOTAL.toLocaleString()} characters used
          </span>
        </div>
        <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full mb-3 overflow-hidden">
          <div
            className={`h-full ${total > MAX_TOTAL * 0.9 ? "bg-red-500" : "bg-[#673AB7]"}`}
            style={{ width: `${Math.min(100, (total / MAX_TOTAL) * 100)}%` }}
          />
        </div>

        {items === null ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">Loading documents...</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            No documents added yet. Paste text or upload a file above.
          </p>
        ) : (
          <div className="space-y-1.5">
            {items.map((i) => (
              <div key={i.id} className={`${rowCls} flex items-center gap-3`}>
                <button
                  className="flex-1 text-left min-w-0"
                  onClick={() => setViewing(i)}
                >
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                    {i.title}
                  </p>
                  <p className="text-[10px] text-gray-400">
                    {i.sourceType} · {(i.characterCount || 0).toLocaleString()}{" "}
                    characters · Added {formatDate(i.addedAt)}
                  </p>
                </button>
                <button
                  onClick={() => remove(i)}
                  title="Delete"
                  className="text-gray-400 hover:text-red-500"
                >
                  <FaTrash size={11} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewing && (
        <Modal
          wide
          title={viewing.title}
          onClose={() => setViewing(null)}
          footer={
            <button className={btnSecondary} onClick={() => setViewing(null)}>
              Close
            </button>
          }
        >
          <pre className="whitespace-pre-wrap break-words text-xs text-gray-700 dark:text-gray-300 cfg-font">
            {viewing.content}
          </pre>
        </Modal>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tab: FAQs                                                           */
/* ------------------------------------------------------------------ */
const emptyFaq = { question: "", answer: "", keywords: "", active: true };

const FaqTab = ({ refresh, askConfirm }) => {
  const [faqs, setFaqs] = useState(null);
  const [form, setForm] = useState(emptyFaq);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setFaqs((await api("/faqs")) || []);
    } catch (e) {
      toast.error(e.message);
      setFaqs([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const resetForm = () => {
    setForm(emptyFaq);
    setEditId(null);
  };

  const payload = () => ({
    question: form.question.trim(),
    answer: form.answer.trim(),
    keywords: form.keywords
      .split(",")
      .map((k) => k.trim())
      .filter(Boolean),
    active: form.active,
  });

  const submit = async () => {
    setBusy(true);
    try {
      if (editId) {
        await api(`/faqs/${editId}`, { method: "PUT", body: payload() });
        toast.success("FAQ updated successfully");
      } else {
        await api("/faqs", { method: "POST", body: payload() });
        toast.success("FAQ added successfully");
      }
      resetForm();
      await load();
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (f) => {
    try {
      await api(`/faqs/${f.id}`, { method: "PUT", body: { active: !f.active } });
      await load();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const remove = (f) =>
    askConfirm({
      title: `Delete "${f.question}"?`,
      message: "This will permanently delete this FAQ. This action cannot be undone.",
      onConfirm: async () => {
        try {
          await api(`/faqs/${f.id}`, { method: "DELETE" });
          toast.success("FAQ deleted successfully");
          if (editId === f.id) resetForm();
          await load();
          refresh();
        } catch (e) {
          toast.error(e.message);
        }
      },
    });

  const startEdit = (f) => {
    setEditId(f.id);
    setForm({
      question: f.question,
      answer: f.answer,
      keywords: (f.keywords || []).join(", "),
      active: f.active,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="space-y-5">
      <div className={`relative ${card}`}>
        <label className="absolute top-4 right-4 flex items-center gap-1.5 cursor-pointer select-none">
          <span className="text-xs text-gray-600 dark:text-gray-300">
            {form.active ? "Active" : "Hidden"}
          </span>
          <Toggle
            checked={form.active}
            onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
          />
        </label>

        <div className="mb-3 pr-24">
          <h3 className={`${headingCls} flex items-center gap-1.5`}>
            <FaQuestionCircle size={12} />
            {editId ? "Edit FAQ" : "Add a FAQ"}
            {(faqs || []).length > 0 && <span className={pill}>{faqs.length}</span>}
          </h3>
          <p className={`${hintCls} mt-1`}>
            Active FAQs appear as quick-reply buttons in the support chat widget and
            return the stored answer instantly.
          </p>
        </div>

        <div className="space-y-3">
          <div>
            <label className={labelCls}>Question</label>
            <input
              type="text"
              placeholder="e.g. How do I add a new lead?"
              value={form.question}
              onChange={(e) => setForm({ ...form, question: e.target.value })}
              className={inputCls}
            />
          </div>
          <div>
            <label className={labelCls}>Answer</label>
            <textarea
              rows={3}
              placeholder="1. Open Leads from the menu..."
              value={form.answer}
              onChange={(e) => setForm({ ...form, answer: e.target.value })}
              className={`${inputCls} resize-y`}
            />
          </div>
          <div>
            <label className={labelCls}>
              Keywords{" "}
              <span className="text-[10px] font-normal text-gray-400">
                (optional, comma-separated)
              </span>
            </label>
            <input
              type="text"
              placeholder="lead, add lead, new lead"
              value={form.keywords}
              onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              className={inputCls}
            />
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              className={btnPrimary}
              disabled={!form.question.trim() || !form.answer.trim() || busy}
              onClick={submit}
            >
              {busy ? "Saving..." : editId ? "Update FAQ" : "Add FAQ"}
            </button>
            {editId && (
              <button className={btnSecondary} onClick={resetForm}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={card}>
        <h4 className="cfg-heading text-xs font-medium mb-2.5 dark:text-gray-300">
          All FAQs
        </h4>
        {faqs === null ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">Loading FAQs...</p>
        ) : faqs.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            No FAQs added yet. Use the form above to add your first one.
          </p>
        ) : (
          <div className="space-y-1.5">
            {faqs.map((f) => (
              <div key={f.id} className={`${rowCls} flex items-start gap-3`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
                    {f.question}
                  </p>
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 line-clamp-2 whitespace-pre-wrap">
                    {f.answer}
                  </p>
                  {f.keywords?.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {f.keywords.map((k) => (
                        <span key={k} className={pill}>
                          {k}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center gap-2.5 flex-shrink-0 pt-0.5">
                  <Toggle checked={!!f.active} onChange={() => toggle(f)} />
                  <button
                    onClick={() => startEdit(f)}
                    title="Edit"
                    className="text-gray-400 hover:text-[#673AB7]"
                  >
                    <FaEdit size={12} />
                  </button>
                  <button
                    onClick={() => remove(f)}
                    title="Delete"
                    className="text-gray-400 hover:text-red-500"
                  >
                    <FaTrash size={11} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tab: Limits                                                         */
/* ------------------------------------------------------------------ */
const LIMIT_RULES = {
  rateLimitPerMinute: [1, 120],
  maxHistoryTurns: [1, 50],
  maxMessageLength: [50, 4000],
};

const LimitsTab = ({ config, save, saving }) => {
  const [f, setF] = useState({});
  useEffect(() => {
    setF({
      rateLimitPerMinute: config.rateLimitPerMinute,
      maxHistoryTurns: config.maxHistoryTurns,
      maxMessageLength: config.maxMessageLength,
    });
  }, [config]);

  const invalid = Object.entries(LIMIT_RULES).some(([k, [min, max]]) => {
    const v = Number(f[k]);
    return !Number.isFinite(v) || v < min || v > max;
  });

  const field = (k, label, hint) => (
    <div>
      <label className={labelCls}>{label}</label>
      <input
        type="number"
        className={inputCls}
        value={f[k] ?? ""}
        onChange={(e) => setF({ ...f, [k]: e.target.value })}
      />
      <p className="text-[11px] text-gray-400 mt-1">{hint}</p>
    </div>
  );

  return (
    <div className={card}>
      <h3 className={headingCls}>Limits</h3>
      <p className={`${hintCls} mt-1 mb-4`}>
        Set usage limits to control cost and keep conversations focused.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {field(
          "rateLimitPerMinute",
          "Rate Limit (per minute)",
          "Messages allowed per user each minute (1–120)."
        )}
        {field(
          "maxHistoryTurns",
          "Max History Turns",
          "Past exchanges the assistant considers (1–50)."
        )}
        {field(
          "maxMessageLength",
          "Max Message Length",
          "Characters allowed per message (50–4000)."
        )}
      </div>
      <div className="pt-4">
        <button
          className={btnPrimary}
          disabled={saving || invalid}
          onClick={() =>
            save({
              rateLimitPerMinute: Number(f.rateLimitPerMinute),
              maxHistoryTurns: Number(f.maxHistoryTurns),
              maxMessageLength: Number(f.maxMessageLength),
            })
          }
        >
          {saving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Tab: Conversations + stats                                          */
/* ------------------------------------------------------------------ */
const StatCard = ({ label, value }) => (
  <div className="p-3 bg-white dark:bg-gray-800 rounded shadow-sm border border-gray-100 dark:border-gray-700">
    <p className="cfg-heading text-lg font-semibold text-[#673AB7] dark:text-[#8B5CF6]">
      {value}
    </p>
    <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
  </div>
);

const ConversationsTab = () => {
  const [stats, setStats] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const size = 20;

  useEffect(() => {
    api("/stats")
      .then(setStats)
      .catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    setData(null);
    api(`/conversations?page=${page}&size=${size}`)
      .then(setData)
      .catch((e) => {
        toast.error(e.message);
        setData({ items: [], page: 0, totalPages: 0, totalElements: 0 });
      });
  }, [page]);

  const open = async (id) => {
    setLoadingDetail(true);
    try {
      setSelected(await api(`/conversations/${id}`));
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoadingDetail(false);
    }
  };

  return (
    <div className="space-y-5">
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Total Conversations" value={stats.totalConversations} />
          <StatCard label="Phone Number Shown" value={stats.callSuggestedConversations} />
          <StatCard label="Resolved by AI" value={stats.resolvedByAiConversations} />
          <StatCard label="Rated Conversations" value={stats.ratedConversations} />
          <StatCard
            label="Average Rating"
            value={
              stats.averageRating != null ? Number(stats.averageRating).toFixed(1) : "-"
            }
          />
        </div>
      )}

      <div className={card}>
        <h3 className={`${headingCls} flex items-center gap-1.5 mb-3`}>
          <FaHistory size={12} /> Conversations
        </h3>

        {selected ? (
          <div>
            <button
              onClick={() => setSelected(null)}
              className="flex items-center gap-1 text-xs text-[#673AB7] dark:text-[#8B5CF6] mb-2 hover:underline"
            >
              <FaChevronLeft size={9} /> Back to all conversations
            </button>
            <p className="text-xs font-medium text-gray-700 dark:text-gray-200">
              {selected.title}
            </p>
            <p className="text-[11px] text-gray-500 dark:text-gray-400 mb-3">
              User ID: {selected.userId}
              {selected.companyId ? ` · Company ID: ${selected.companyId}` : ""} ·
              Last activity: {formatDate(selected.lastActivityAt)}
              {selected.rating ? ` · Rating: ${selected.rating}/5` : ""}
            </p>
            {selected.ratingComment && (
              <p className="text-xs px-2.5 py-1.5 mb-3 bg-[#F3E8FF] dark:bg-purple-900/40 text-[#4C2D85] dark:text-[#E9D5FF] rounded">
                User feedback: {selected.ratingComment}
              </p>
            )}
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {(selected.messages || []).map((msg, idx) => (
                <div
                  key={idx}
                  className={`max-w-[85%] px-2.5 py-1.5 rounded-lg text-xs ${
                    msg.role === "user"
                      ? "ml-auto bg-[#F3E8FF] dark:bg-purple-900/40 text-[#4C2D85] dark:text-[#E9D5FF]"
                      : "bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100"
                  }`}
                >
                  <p style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                    {msg.text}
                  </p>
                  <p className="text-[9px] text-gray-400 mt-1">
                    {msg.role === "model" ? `${msg.source} · ` : ""}
                    {formatDate(msg.timestamp)}
                  </p>
                </div>
              ))}
            </div>
          </div>
        ) : data === null || loadingDetail ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            Loading conversations...
          </p>
        ) : data.items.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500">
            No conversations yet.
          </p>
        ) : (
          <>
            <div className="space-y-1.5">
              {data.items.map((c) => (
                <button
                  key={c.id}
                  onClick={() => open(c.id)}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2 bg-gray-50 dark:bg-gray-700/50 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-left"
                >
                  <div className="min-w-0">
                    <p className="text-xs font-medium text-gray-700 dark:text-gray-200 truncate">
                      {c.userName} · {c.title}
                    </p>
                    <p className="text-[10px] text-gray-400">
                      {c.accountType}
                      {c.companyName ? ` · ${c.companyName}` : ""} · {c.messageCount}{" "}
                      messages · Last active {formatDate(c.lastActivityAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {c.callSuggested && <span className={pill}>Phone number shown</span>}
                    {c.rating ? (
                      <span className={pill}>{c.rating}/5</span>
                    ) : null}
                    <FaChevronRight size={10} className="text-gray-400" />
                  </div>
                </button>
              ))}
            </div>

            <div className="flex items-center justify-between mt-4">
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                {data.totalElements} total · Page {data.page + 1} of{" "}
                {Math.max(1, data.totalPages)}
              </span>
              <div className="flex gap-2">
                <button
                  className={btnSecondary}
                  disabled={page === 0}
                  onClick={() => setPage((p) => p - 1)}
                >
                  Previous
                </button>
                <button
                  className={btnSecondary}
                  disabled={page + 1 >= data.totalPages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Main component                                                      */
/* ------------------------------------------------------------------ */
const TABS = [
  { id: "general", label: "General", icon: <FaCog size={11} /> },
  { id: "phone", label: "Support Contact", icon: <FaPhoneAlt size={11} /> },
  { id: "key", label: "API Key", icon: <FaKey size={11} /> },
  { id: "knowledge", label: "Knowledge Base", icon: <FaDatabase size={11} /> },
  { id: "faqs", label: "FAQs", icon: <FaQuestionCircle size={11} /> },
  { id: "limits", label: "Limits", icon: <FaSlidersH size={11} /> },
  { id: "conversations", label: "Conversations", icon: <FaHistory size={11} /> },
];

const Adminchat = () => {
  const { user } = useContext(AuthContext);
  const isAllowed = user?.accountType === "SUPER_SUPER_ADMIN";

  const [tab, setTab] = useState(() => {
    if (typeof window !== "undefined") {
      return sessionStorage.getItem("supportSettingsTab") || "general";
    }
    return "general";
  });
  const [data, setData] = useState(null); // { config, hasApiKey, maskedApiKey, apiKeyError }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    sessionStorage.setItem("supportSettingsTab", tab);
  }, [tab]);

  const loadConfig = useCallback(async () => {
    try {
      setData(await api("/config"));
    } catch (e) {
      toast.error(e.status === 403 ? "You do not have access to this page." : e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAllowed) loadConfig();
  }, [isAllowed, loadConfig]);

  // PUT /config with only the changed fields
  const saveConfig = async (fields) => {
    setSaving(true);
    try {
      setData(await api("/config", { method: "PUT", body: fields }));
      toast.success("Support settings saved successfully");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runConfirm = async () => {
    setConfirming(true);
    try {
      await confirm.onConfirm();
    } finally {
      setConfirming(false);
      setConfirm(null);
    }
  };

  const fontStyle = (
    <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700&family=Inter:wght@400;500;600&display=swap'); .cfg-heading{font-family:'Sora',sans-serif;} .cfg-font{font-family:'Inter',sans-serif;}`}</style>
  );

  if (!isAllowed) {
    return (
<div className="mx-auto bg-[#FAF8FF] dark:bg-gray-900 p-5 rounded-xl shadow-sm min-h-screen cfg-font flex items-center justify-center">        {fontStyle}
        <div className="text-center">
          <FaShieldAlt size={28} className="text-[#8B5CF6] mx-auto mb-3" />
          <p className={`${headingCls} mb-1`}>Access Restricted</p>
          <p className={hintCls}>
            Support Settings are available to Super Super Admin accounts only.
          </p>
        </div>
      </div>
    );
  }

  const config = data?.config;

  return (
  <div className="mx-auto bg-[#FAF8FF] dark:bg-gray-900 p-5 rounded-xl shadow-sm transition-all duration-200 min-h-screen cfg-font">
    {fontStyle}
      {/* Header */}
      <div className="flex justify-between items-center mb-5">
        <div>
          <h2
            className="text-xl font-bold bg-gradient-to-r from-[#8B5CF6] to-[#673AB7] bg-clip-text text-transparent"
            style={{ fontFamily: "'Sora', sans-serif" }}
          >
            Support Settings
          </h2>
          <p className={`${hintCls} mt-1`}>
            Manage the AI support assistant, its knowledge base, FAQs and user
            conversations.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1.5 mb-4 border-b border-gray-200 dark:border-gray-700 max-w-5xl">
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-t-lg transition-colors ${
              tab === t.id
                ? "text-[#673AB7] dark:text-[#8B5CF6] border-b-2 border-[#673AB7] dark:border-[#8B5CF6] bg-white dark:bg-gray-800"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      <div className={tab === "conversations" ? "max-w-5xl" : "max-w-3xl"}>
        {loading ? (
          <LoadingBox text="Loading support settings..." />
        ) : !config ? (
          <LoadingBox text="Unable to load support settings. Please refresh the page." />
        ) : (
          <>
            {tab === "general" && (
              <GeneralTab config={config} save={saveConfig} saving={saving} />
            )}
            {tab === "phone" && (
              <PhoneTab config={config} save={saveConfig} saving={saving} />
            )}
            {tab === "key" && (
              <KeyTab keyInfo={data} refresh={loadConfig} askConfirm={setConfirm} />
            )}
            {tab === "knowledge" && (
              <KnowledgeTab refresh={loadConfig} askConfirm={setConfirm} />
            )}
            {tab === "faqs" && <FaqTab refresh={loadConfig} askConfirm={setConfirm} />}
            {tab === "limits" && (
              <LimitsTab config={config} save={saveConfig} saving={saving} />
            )}
            {tab === "conversations" && <ConversationsTab />}
          </>
        )}
      </div>

      {/* Confirmation modal */}
      {confirm && (
        <Modal
          title={confirm.title}
          onClose={() => !confirming && setConfirm(null)}
          footer={
            <>
              <button
                onClick={() => setConfirm(null)}
                disabled={confirming}
                className="px-3 py-1.5 text-xs rounded border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={runConfirm}
                disabled={confirming}
                className="px-3 py-1.5 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {confirming ? "Deleting..." : "Delete"}
              </button>
            </>
          }
        >
          <p className="text-xs text-gray-500 dark:text-gray-400">{confirm.message}</p>
        </Modal>
      )}
    </div>
  );
};

export default Adminchat;